import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import {
  PostEmploymentSettingsPayload,
  saveEmploymentSettings
} from 'RestAPIs/employment-settings';

import { scenarioAtom } from '../atoms';
import {
  selectedCostCenterAtom,
  selectedFunctionAtom,
  updateEmploymentSettingsAtom
} from './atoms';

import { Box } from '@mui/material';

import {
  LoadingOverlay,
  showSnackbar,
  SkeletonWithIcon
} from 'components/shared';

import NoScenario from '../components/NoScenario';
import CapacityGraph from './components/CapacityGraph';
import CapacityTable from './components/CapacityTable';
import FormationViewSettings from './components/FormationViewSettings';

import { useGetCapacityQuery } from './queries';

const FormationTab = () => {
  const { t } = useTranslation('common');
  const scenario = useRecoilValue(scenarioAtom);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [selectedFunction, setSelectedFunction] =
    useRecoilState(selectedFunctionAtom);
  const [selectedCostCenter, setSelectedCostCenter] = useRecoilState(
    selectedCostCenterAtom
  );

  const { data: capacity, refetch } = useGetCapacityQuery({
    selectedCostCenterId: selectedCostCenter?.id,
    selectedFunctionId: selectedFunction?.id
  });

  const setUpdateEmploymentSettingsFn = useSetRecoilState(
    updateEmploymentSettingsAtom
  );

  const reloadFormation = async () => {
    if (scenario?.scenarioId) {
      if (!capacity) {
        setIsLoading(true); // Only show loading state at initial fetch
      }

      await refetch();
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setSelectedCostCenter(null);
    setSelectedFunction(null);
  }, [scenario]);

  useEffect(() => {
    const updateEmployeeSettings = async (
      payload: PostEmploymentSettingsPayload
    ) => {
      if (scenario?.scenarioId && payload) {
        try {
          await saveEmploymentSettings({
            payload,
            scenarioId: scenario.scenarioId
          });
          showSnackbar({
            severity: 'success',
            message: t('changesSaved')
          });
          await reloadFormation();
        } catch {
          showSnackbar({
            severity: 'error',
            message: t('somethingWentWrong')
          });
        }
      }
    };

    setUpdateEmploymentSettingsFn(() => updateEmployeeSettings);
  }, [scenario, reloadFormation]);

  if (!scenario) {
    return <NoScenario />;
  }

  return (
    <>
      <LoadingOverlay loading={isLoading} />
      <Box
        position="relative"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={4}
      >
        <Box ml="auto">
          <FormationViewSettings capacity={capacity} />
        </Box>
      </Box>
      <Box mb={4}>
        {isLoading ? (
          <SkeletonWithIcon height={400} />
        ) : (
          <CapacityGraph capacity={capacity} />
        )}
      </Box>
      <CapacityTable capacity={capacity} />
    </>
  );
};

export default FormationTab;
